import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
} from '@mui/material';
import './itemdetail.css';
import { api, API_BASE, AI_BASE, getAuth } from '../api';
import { buildItemUrl } from '../utils/itemUrl';
import { getDisplayImageUrl } from '../utils/imageUrl';
import { formatPrice } from '../utils/price';
import PaginationControls from './PaginationControls';
import usePageTitle from '../hooks/usePageTitle';
import { MODERATION_REJECTION_MESSAGE, moderateTextFields } from '../utils/textModeration';
import { usePriceVisibility } from '../contexts/PriceVisibilityContext';

const AI_URL = (AI_BASE || API_BASE).replace(/\/+$/, '');
const API_URL = (API_BASE || '').replace(/\/+$/, '');

const num = (v) => (v === null || v === undefined ? null : Number(v));
const dollarsFromCents = (c) => {
  const n = num(c);
  return Number.isFinite(n) ? n / 100 : null;
};

export const getPriceDollars = (obj) => {
  if (!obj) return null;
  if (obj.price_cents != null) return dollarsFromCents(obj.price_cents);
  if (obj.cents != null) return dollarsFromCents(obj.cents);
  const raw = obj.price;
  if (raw == null) return null;
  const s = String(raw).trim();
  const n = Number(s.replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) return null;
  return s.includes('.') ? n : n / 100;
};

const readStoredUser = () => {
  const LS = window.localStorage;
  const SS = window.sessionStorage;
  const raw = LS.getItem('user') || SS.getItem('user');
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const ALLOWED_ITEMS_PER_PAGE = [12, 24, 36];
const EVENT_SEARCH_COPY = {
  'stanley-cup-finals': {
    title: 'Stanley Cup Finals Collectibles',
    subtitle: 'Showing Carolina Hurricanes or Vegas Golden Knights items tied to the Stanley Cup Finals spotlight.',
  },
  'nba-finals': {
    title: 'NBA Finals Collectibles',
    subtitle: 'Showing San Antonio Spurs or New York Knicks items tied to the NBA Finals spotlight.',
  },
};

const normalizeItemsPerPagePreference = (value) => {
  const parsed = Number(value);
  return ALLOWED_ITEMS_PER_PAGE.includes(parsed) ? parsed : 12;
};
const safeText = (value) => (typeof value === 'string' ? value : String(value ?? ''));
const getStoredItemsPerPagePreference = () => {
  const storedUser = readStoredUser();
  if (storedUser && storedUser.items_per_page_preference != null) {
    const userPref = normalizeItemsPerPagePreference(storedUser.items_per_page_preference);
    if (ALLOWED_ITEMS_PER_PAGE.includes(userPref)) return userPref;
  }
  const raw = window.localStorage.getItem('items_per_page_preference');
  const localPref = normalizeItemsPerPagePreference(raw);
  return ALLOWED_ITEMS_PER_PAGE.includes(localPref) ? localPref : 12;
};

const SearchItems = () => {
  const { showPrices: pricePreferenceEnabled, pricesLoaded } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;
  usePageTitle('Search');
  const location = useLocation();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState('newest');
  useEffect(() => {
    if (!showPrices && ['high', 'low'].includes(sortOption)) setSortOption('newest');
  }, [showPrices, sortOption]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [moderationError, setModerationError] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [signedOnly, setSignedOnly] = useState(false);
  const [mobileSearchMenuOpen, setMobileSearchMenuOpen] = useState(false);
  const [debouncedBrandFilter, setDebouncedBrandFilter] = useState('');
  const [debouncedCategoryFilter, setDebouncedCategoryFilter] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(() => getStoredItemsPerPagePreference());
  const [currentUser, setCurrentUser] = useState(() => getAuth().user || null);
  const [wishlistMap, setWishlistMap] = useState({});
  const currentItems = searchResults;

  const skipNextFetchRef = useRef(false);
  const currentPageRef = useRef(currentPage);
  const handledCreateModeRef = useRef('');

  const queryParams = new URLSearchParams(location.search);
  const createMode = queryParams.get('create');
  const query =
    queryParams.get('q') ||
    queryParams.get('query') ||
    '';
  const urlCategory = queryParams.get('category') || '';
  const qMode = queryParams.get('qMode') || '';
  const eventKey = queryParams.get('event') || '';
  const eventCopy = EVENT_SEARCH_COPY[eventKey] || null;
  const isAllQuery = ['__all__', 'all'].includes((query || '').trim().toLowerCase());
  const activeBrandFilter = debouncedBrandFilter.trim();
  const activeCategoryFilter = debouncedCategoryFilter.trim();
  const category = categoryFilter.trim();
  const hasSearchContext = Boolean(query || isAllQuery || activeBrandFilter || activeCategoryFilter || signedOnly);
  const activeFilterSummary = [
    activeBrandFilter ? `Brand: ${activeBrandFilter}` : null,
    activeCategoryFilter ? `Category: ${activeCategoryFilter}` : null,
    signedOnly ? 'Signed only' : null,
  ].filter(Boolean);

  useEffect(() => {
    if (createMode !== 'ai') return;
    const key = location.search;
    if (handledCreateModeRef.current === key) return;
    handledCreateModeRef.current = key;
    setConfirmText('');
    setModerationError('');
    setConfirmOpen(true);
  }, [createMode, location.search]);

  useEffect(() => {
    setCategoryFilter(urlCategory);
  }, [urlCategory]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBrandFilter(brandFilter), 300);
    return () => clearTimeout(timer);
  }, [brandFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCategoryFilter(categoryFilter), 300);
    return () => clearTimeout(timer);
  }, [categoryFilter]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [query, urlCategory, currentPage, activeBrandFilter, activeCategoryFilter, signedOnly]);

  useEffect(() => {
    setConfirmText(query || '');
  }, [query]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const syncAuthUser = () => {
      const { user } = getAuth();
      setCurrentUser(user || null);
    };
    syncAuthUser();
    window.addEventListener('storage', syncAuthUser);
    return () => window.removeEventListener('storage', syncAuthUser);
  }, []);

  useEffect(() => {
    if (currentPageRef.current !== 1) {
      skipNextFetchRef.current = true;
      setCurrentPage(1);
    }
  }, [query, urlCategory, activeBrandFilter, activeCategoryFilter, signedOnly]);

  useEffect(() => {
    if (!query && !isAllQuery && !activeBrandFilter && !activeCategoryFilter && !signedOnly) {
      setSearchResults([]);
      setTotalItems(0);
      setTotalPages(1);
      setModerationError('');
      return;
    }
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    (async () => {
      try {
        const moderationResult = moderateTextFields({
          query,
          category: activeCategoryFilter,
          brand: activeBrandFilter,
        });
        if (!moderationResult.ok) {
          setModerationError(MODERATION_REJECTION_MESSAGE);
          setSearchResults([]);
          setTotalItems(0);
          setTotalPages(1);
          return;
        }

        setModerationError('');
        const qs = new URLSearchParams();
        if (query) qs.set('q', query);
        if (qMode) qs.set('qMode', qMode);
        if (eventKey) qs.set('event', eventKey);
        if (activeBrandFilter) qs.set('brand', activeBrandFilter);
        if (activeCategoryFilter) qs.set('category', activeCategoryFilter);
        if (signedOnly) qs.set('signed', '1');
        qs.set('page', String(currentPage));
        qs.set('limit', String(itemsPerPage));
        qs.set('sort', sortOption);

        const res = await fetch(`${API_URL}/api/search?${qs.toString()}`);
        if (!res.ok) throw new Error(`Failed to fetch search results (HTTP ${res.status})`);
        const data = await res.json();

        const items = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : Array.isArray(data?.results)
              ? data.results
              : [];

        setSearchResults(items);
        const total = Number(data?.totalItems ?? items.length ?? 0);
        const pages = Number(data?.totalPages);
        setTotalItems(Number.isFinite(total) ? total : 0);
        setTotalPages(
          Number.isFinite(pages)
            ? pages
            : Math.max(1, Math.ceil((Number.isFinite(total) ? total : 0) / itemsPerPage))
        );
      } catch (err) {
        console.error(err);
        alert('An error occurred while fetching search results.');
        setSearchResults([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    })();
  }, [query, qMode, eventKey, isAllQuery, activeBrandFilter, activeCategoryFilter, signedOnly, currentPage, sortOption, itemsPerPage]);

  useEffect(() => {
    const userId = currentUser?.id ?? currentUser?.user_id ?? null;
    const visibleItemIds = Array.from(
      new Set(
        (currentItems || [])
          .map((item) => Number(item?.item_id))
          .filter((itemId) => Number.isFinite(itemId) && itemId > 0)
      )
    );

    if (!userId || !visibleItemIds.length) {
      setWishlistMap({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/wishlist/checks', {
          params: { itemIds: visibleItemIds.join(',') },
        });
        if (cancelled) return;
        setWishlistMap(data?.wishlist_map || {});
      } catch (err) {
        if (!cancelled) {
          console.warn('Wishlist batch check failed', err);
          setWishlistMap({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentItems, currentUser?.id, currentUser?.user_id]);

  const handleClearFilters = () => {
    setBrandFilter('');
    setCategoryFilter('');
    setDebouncedBrandFilter('');
    setDebouncedCategoryFilter('');
    setSignedOnly(false);
    setSortOption('newest');
    setCurrentPage(1);
    const nextParams = new URLSearchParams();
    if (query && !isAllQuery) {
      nextParams.set('q', query);
    } else {
      nextParams.set('q', '__all__');
    }
    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
  };

  const persistItemsPerPagePreference = async (nextValue) => {
    const normalized = normalizeItemsPerPagePreference(nextValue);
    setItemsPerPage(normalized);
    setCurrentPage(1);
    window.localStorage.setItem('items_per_page_preference', String(normalized));

    const storedUser = readStoredUser();
    if (storedUser) {
      const merged = { ...storedUser, items_per_page_preference: normalized };
      window.localStorage.setItem('user', JSON.stringify(merged));
      window.sessionStorage.setItem('user', JSON.stringify(merged));
    }

    const userId = storedUser?.id ?? storedUser?.user_id;
    if (!userId) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/preferences`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items_per_page_preference: normalized }),
      });
      if (!response.ok) {
        throw new Error(`Preference save failed (${response.status})`);
      }
      const data = await response.json().catch(() => ({}));
      const savedValue = normalizeItemsPerPagePreference(data?.items_per_page_preference);
      const merged = { ...storedUser, items_per_page_preference: savedValue };
      window.localStorage.setItem('user', JSON.stringify(merged));
      window.sessionStorage.setItem('user', JSON.stringify(merged));
      window.localStorage.setItem('items_per_page_preference', String(savedValue));
      window.sessionStorage.setItem('items_per_page_preference', String(savedValue));
      setItemsPerPage(savedValue);
    } catch (err) {
      console.warn('Failed to persist items_per_page_preference', err);
    }
  };

  const handleAddToCollection = async (item) => {
    const user = readStoredUser();
    const userId = user?.id ?? user?.user_id ?? null;
    if (!userId) {
      alert('Please log in to add items to your collection.');
      navigate('/login');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/add-to-collection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, itemId: item.item_id }),
      });
      alert(res.ok ? 'Item added!' : 'Failed to add.');
    } catch {
      alert('Could not connect to the server.');
    }
  };

  const handleWishlistToggle = async (item, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const user = currentUser || getAuth().user || null;
    const userId = user?.id ?? user?.user_id ?? null;
    if (!userId) {
      navigate('/login');
      return;
    }

    const itemId = Number(item?.item_id);
    if (!Number.isFinite(itemId) || itemId <= 0) return;

    const wishlisted = Boolean(wishlistMap[itemId]);
    try {
      if (wishlisted) {
        await api.delete(`/api/wishlist/remove/${itemId}`);
        setWishlistMap((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
      } else {
        await api.post('/api/wishlist/add', { item_id: itemId });
        setWishlistMap((prev) => ({ ...prev, [itemId]: true }));
      }
    } catch (err) {
      console.error('Wishlist toggle failed:', err);
      alert('Could not update wishlist.');
    }
  };

  const parseJsonSafe = (t) => {
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  };

  const summarizeError = (status, data, fallbackMsg) => {
    const base = data?.error || data?.message || fallbackMsg || 'Unknown error';
    const src = data?.source ? ` (source: ${data.source})` : '';
    const code = data?.code ? ` [${data.code}]` : '';
    return `Failed (${status})${src}${code}: ${base}`;
  };

  const runAiSubmit = async () => {
    const trimmedQuery = (confirmText || '').trim();
    if (!trimmedQuery || processing) return;
    const moderationResult = moderateTextFields({ query: trimmedQuery });
    if (!moderationResult.ok) {
      setModerationError(MODERATION_REJECTION_MESSAGE);
      alert(MODERATION_REJECTION_MESSAGE);
      return;
    }
    setModerationError('');

    const hasMultipleWords = trimmedQuery.split(/\s+/).length >= 2;
    const hasNumber = /\d/.test(trimmedQuery);
    if (!hasMultipleWords && !hasNumber) {
      alert("Please include at least two words or a set number like '097/123'.");
      return;
    }

    setConfirmOpen(false);
    setProcessing(true);
    const ctrl = new AbortController();
    const timeoutMs = 95_000;
    const t = setTimeout(() => ctrl.abort(), timeoutMs);

    try {
      const r = await fetch(`${AI_URL}/process_item?force=1`, {
        method: 'POST',
        body: JSON.stringify({ item_input: trimmedQuery, query: trimmedQuery }),
        headers: { 'Content-Type': 'application/json' },
        signal: ctrl.signal,
      });

      const text = await r.text();
      const data = parseJsonSafe(text) || {};

      if (!r.ok) {
        const msg = summarizeError(r.status, data, data?.detail || 'AI service error');
        alert(msg);
        return;
      }

      const payload = data.data || data;
      const itemId = payload.item_id || payload.id;

      if (itemId) {
        setConfirmOpen(false);
        navigate(buildItemUrl({ item_id: itemId, name: payload?.name || payload?.item_name || trimmedQuery }));
        return;
      }

      alert('AI did not return an item id; falling back to manual add.');
      navigate(`/add?query=${encodeURIComponent(trimmedQuery)}`);
    } catch {
      alert('The AI service took too long or failed. Please try again.');
    } finally {
      clearTimeout(t);
      setProcessing(false);
    }
  };

  const handleFinalConfirm = () => {
    const trimmedQuery = (query || '').trim();
    if (!trimmedQuery) return;
    setConfirmText(trimmedQuery);
    setConfirmOpen(true);
  };

  return (
    <Box>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
          pb: '60px',
        }}
      >
        {moderationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {safeText(moderationError)}
          </Alert>
        )}

        {hasSearchContext && (
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2.25 },
              mb: { xs: 2, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.25, sm: 2 },
              alignItems: 'stretch',
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 8px 24px rgba(15, 26, 46, 0.08)',
              maxWidth: 1440,
              mx: 'auto',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'stretch',
                gap: { xs: 1, sm: 2 },
                width: '100%',
              }}
            >
              <Typography
                component="h2"
                variant="subtitle1"
                align="center"
                sx={{
                  width: '100%',
                  flex: '0 0 auto',
                  minWidth: 0,
                  mb: 0,
                  lineHeight: 1.25,
                  fontWeight: 700,
                  color: '#0f1a2e',
                  textAlign: 'center',
                }}
              >
                {eventCopy ? (
                  <>
                    <Box component="span" sx={{ display: 'block' }}>
                      {eventCopy.title}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: '0.9rem',
                        opacity: 0.72,
                        fontWeight: 600,
                        mt: 0.25,
                      }}
                    >
                      {eventCopy.subtitle}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: '0.9rem',
                        opacity: 0.72,
                        fontWeight: 600,
                        mt: 0.25,
                      }}
                    >
                      {totalItems} {totalItems === 1 ? 'result' : 'results'} found
                    </Box>
                  </>
                ) : totalItems === 0 ? (
                  <>
                    {`No results found${query ? ` for "${safeText(query)}"` : ''}.`}
                    {activeFilterSummary.length > 0 && (
                      <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
                        {activeFilterSummary.map((label) => (
                          <Box component="span" key={label} sx={{ display: 'block' }}>
                            {label}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    {isAllQuery ? (
                      <>
                        <Box component="span" sx={{ display: 'block' }}>
                          Showing all items
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            fontSize: '0.9rem',
                            opacity: 0.72,
                            fontWeight: 600,
                            mt: 0.25,
                          }}
                        >
                          ({totalItems} total)
                        </Box>
                      </>
                    ) : (
                      `${totalItems} ${totalItems === 1 ? 'result' : 'results'} found${query ? ` for "${safeText(query)}"` : ''}`
                    )}
                    {category ? ` in category "${safeText(category)}"` : ''}
                  </>
                )}
              </Typography>

              <Button
                variant="outlined"
                onClick={() => setMobileSearchMenuOpen((prev) => !prev)}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  height: 44,
                  width: '100%',
                  justifyContent: 'center',
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: 'none',
                  bgcolor: mobileSearchMenuOpen ? 'rgba(15, 26, 46, 0.04)' : 'transparent',
                }}
              >
                {mobileSearchMenuOpen
                  ? 'Hide Search Options'
                  : activeFilterSummary.length > 0
                    ? `Show Search Options (${activeFilterSummary.length} active)`
                    : 'Show Search Options'}
              </Button>

              <Box
                sx={{
                  display: {
                    xs: mobileSearchMenuOpen ? 'flex' : 'none',
                    md: 'flex',
                  },
                  flexDirection: 'column',
                  gap: { xs: 1.25, sm: 2 },
                  width: '100%',
                  pt: { xs: 1, md: 0 },
                  borderTop: { xs: '1px solid rgba(0,0,0,0.08)', md: 'none' },
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: '180px 180px',
                    },
                    gap: { xs: 1.25, sm: 2 },
                    width: '100%',
                    maxWidth: 980,
                    mx: 'auto',
                    justifyContent: { md: 'center' },
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/search?q=__all__')}
                    fullWidth={false}
                    sx={{
                      height: 46,
                      width: { xs: '100%', md: 'auto' },
                      minWidth: { md: 140 },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                    }}
                  >
                    View All Items
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    fullWidth
                    sx={{
                      height: 46,
                      width: '100%',
                      minWidth: { md: 180 },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'minmax(0, 1fr) minmax(0, 1fr) 160px 180px 180px',
                    },
                    gap: { xs: 1.25, sm: 2 },
                    width: '100%',
                    maxWidth: 980,
                    mx: 'auto',
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    label="Brand"
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 80 }}
                    sx={{
                      '& .MuiInputBase-root': {
                        minHeight: 46,
                        borderRadius: 2,
                      },
                      width: '100%',
                    }}
                  />
                  <TextField
                    label="Category"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 80 }}
                    sx={{
                      '& .MuiInputBase-root': {
                        minHeight: 46,
                        borderRadius: 2,
                      },
                      width: '100%',
                    }}
                  />
                  <FormControl
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        minHeight: 46,
                        borderRadius: 2,
                      },
                      width: '100%',
                    }}
                  >
                    <InputLabel>Items Per Page</InputLabel>
                    <Select
                      value={itemsPerPage}
                      label="Items Per Page"
                      onChange={(e) => persistItemsPerPagePreference(Number(e.target.value))}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      {ALLOWED_ITEMS_PER_PAGE.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    size="small"
                    fullWidth
                    sx={{
                      width: '100%',
                      minWidth: { md: 170 },
                      '& .MuiInputBase-root': {
                        minHeight: 46,
                        borderRadius: 2,
                      },
                    }}
                  >
                    <InputLabel>Sort Items</InputLabel>
                    <Select
                      value={sortOption}
                      label="Sort Items"
                      onChange={(e) => {
                        setSortOption(e.target.value);
                        setCurrentPage(1);
                      }}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      <MenuItem value="newest">Newest Added</MenuItem>
                      <MenuItem value="oldest">Oldest Added</MenuItem>
                      {showPrices && <MenuItem value="high">Highest Value</MenuItem>}
                      {showPrices && <MenuItem value="low">Lowest Value</MenuItem>}
                      <MenuItem value="az">A-Z</MenuItem>
                      <MenuItem value="za">Z-A</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      minHeight: 46,
                      width: '100%',
                      minWidth: { md: 180 },
                      px: 1.25,
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: 2,
                      alignItems: 'center',
                      bgcolor: signedOnly ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
                    }}
                    control={
                      <Checkbox
                        checked={signedOnly}
                        onChange={(e) => setSignedOnly(e.target.checked)}
                      />
                    }
                    label="Signed only"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {hasSearchContext && totalItems === 0 ? null : hasSearchContext ? (
          <>
            <Grid container spacing={3}>
              {currentItems.map((item) => {
                const priceDollars = getPriceDollars(item);
                const itemId = Number(item?.item_id);
                const isWishlisted = Boolean(wishlistMap[itemId]);
                return (
                  <Grid item xs={12} sm={6} md={4} key={item.item_id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={getDisplayImageUrl(item)}
                        alt={item.name || 'Item'}
                        sx={{ objectFit: 'contain', backgroundColor: '#f8f8f8' }}
                      />
                      <CardContent>
                        <Typography variant="h6">{item.name || 'Unnamed Item'}</Typography>
                        {showPrices && <Typography variant="subtitle1" sx={{ color: 'primary.light' }}>
                          Value: {formatPrice({ price: priceDollars != null ? priceDollars.toFixed(2) : null }) || 'TBD'}
                        </Typography>}
                        {item.category && (
                          <Typography variant="body2" color="text.secondary">
                            Category: {item.category}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions sx={{ mt: 'auto', px: 2, pb: 2 }}>
                        <Box
                        sx={{
                          width: '100%',
                          backgroundColor: '#F1F4F7',
                          borderRadius: 3,
                          border: '1px solid rgba(0,0,0,0.05)',
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                            width: '100%',
                          }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleAddToCollection(item)}
                            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' }, minWidth: 0, height: 42 }}
                          >
                            Add to Collection
                          </Button>
                          <Button
                            variant={isWishlisted ? 'outlined' : 'contained'}
                            onClick={(event) => handleWishlistToggle(item, event)}
                            sx={{
                              flex: 1,
                              width: { xs: '100%', sm: 'auto' },
                              minWidth: 0,
                              height: 42,
                              bgcolor: isWishlisted ? 'transparent' : '#7B3FB3',
                              color: isWishlisted ? '#7B3FB3' : '#fff',
                              borderColor: '#7B3FB3',
                              '&:hover': {
                                bgcolor: isWishlisted ? 'rgba(123, 63, 179, 0.08)' : '#6D35A3',
                                borderColor: '#6D35A3',
                              },
                            }}
                          >
                            {isWishlisted ? 'Wishlisted' : 'Add to Wishlist'}
                          </Button>
                        </Box>
                        <Box sx={{ width: '100%' }}>
                          <Button
                            component={Link}
                            to={buildItemUrl(item)}
                            variant="contained"
                            color="info"
                            sx={{ width: '100%', height: 42 }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        ) : null}

        {hasSearchContext && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mt: { xs: 2, sm: 4 },
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={processing}
              onClick={handleFinalConfirm}
              sx={{ px: 4, py: 1.5 }}
            >
              {processing ? 'Processing...' : "Can't Find Your Item? Add It with AI"}
            </Button>

            <Button
              component={Link}
              to={`/add?query=${encodeURIComponent(query)}`}
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                whiteSpace: 'normal',
                lineHeight: 1.2,
                minHeight: 56,
              }}
            >
              Low numbered, rare, or signed? Manually add it
            </Button>
          </Box>
        )}
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Use AI to add this item?</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Be as specific as possible to improve pricing and matching.
          </Typography>
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Examples
            </Typography>
            <Typography variant="body2">Cards: Connor McDavid Young Guns #201 2015-16 Upper Deck Series 1</Typography>
            <Typography variant="body2">Pops: Sonic 06 Funko Pop</Typography>
            <Typography variant="body2">Memorabilia: Zach Hyman Signed Oilers Adidas Heritage Classic Jersey</Typography>
            <Typography variant="body2">Lego: Gingerbread AT-AT Walker Lego Star Wars 40806</Typography>
          </Box>
          <TextField
            label="AI search text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={runAiSubmit} disabled={processing}>
            {processing ? 'Working...' : 'Confirm & Run AI'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SearchItems;
